/**
 * 動的クライアント登録（RFC 7591）。
 *
 * admin の認可サーバーは固定の client_id を配らない。client_id は登録メタデータを
 * 署名した JWS で、`POST /register`（認証不要）で発行される。初回に 1 回登録し、
 * 得た client_id を localStorage へ置いて使い回す。
 *
 * **client_id を localStorage に置くのは、これが秘密ではないから**である
 * （公開クライアントの識別子で、漏れても PKCE と redirect_uri の完全一致が
 * 守りになる）。トークンは別で、こちらは絶対に置かない。
 *
 * 登録が拒否された場合（発行した鍵が変わった・登録が失効した）は、
 * キャッシュを捨ててもう一度登録する。
 */

export type ClientRegistrationRequest = {
  registrationEndpoint: string;
  clientName: string;
  redirectUris: string[];
  scope: string;
  fetchImpl?: typeof fetch;
};

export class RegistrationError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "RegistrationError";
  }
}

/** redirect_uris は最大 5 本・各 512 文字まで（認可サーバー側の制限） */
const MAX_REDIRECT_URIS = 5;
const MAX_REDIRECT_URI_LENGTH = 512;

const CACHE_PREFIX = "visumo.admin.oauth.client";

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * キャッシュの鍵。issuer・redirect_uri・scope のどれかが変われば別の登録になる
 * （古い登録を使い回して `invalid_client` を踏まないようにする）。
 */
export function registrationCacheKey(input: {
  issuer: string;
  redirectUri: string;
  scope: string;
}): string {
  return `${CACHE_PREFIX}:${input.issuer}:${input.redirectUri}:${input.scope}`;
}

export function readCachedClientId(key: string): string | null {
  const value = safeLocalStorage()?.getItem(key) ?? null;
  return value === "" ? null : value;
}

export function writeCachedClientId(key: string, clientId: string): void {
  safeLocalStorage()?.setItem(key, clientId);
}

export function clearCachedClientId(key: string): void {
  safeLocalStorage()?.removeItem(key);
}

export async function registerClient(
  input: ClientRegistrationRequest,
): Promise<string> {
  const redirectUris = input.redirectUris.slice(0, MAX_REDIRECT_URIS);
  const tooLong = redirectUris.find(
    (uri) => uri.length > MAX_REDIRECT_URI_LENGTH,
  );
  if (tooLong !== undefined) {
    throw new RegistrationError(
      "redirect_uri が長すぎます",
      "invalid_redirect_uri",
    );
  }
  const fetchImpl = input.fetchImpl ?? fetch;
  const response = await fetchImpl(input.registrationEndpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify({
      client_name: input.clientName,
      redirect_uris: redirectUris,
      // 公開クライアント。secret を持たせない（持たせられる場所が無い）
      token_endpoint_auth_method: "none",
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
      application_type: "web",
      scope: input.scope,
    }),
  });
  let payload: Record<string, unknown> = {};
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    payload = {};
  }
  if (!response.ok) {
    const code =
      typeof payload["error"] === "string"
        ? payload["error"]
        : `http_${response.status}`;
    const description =
      typeof payload["error_description"] === "string"
        ? payload["error_description"]
        : `クライアント登録が ${response.status} を返しました`;
    throw new RegistrationError(description, code);
  }
  const clientId = payload["client_id"];
  if (typeof clientId !== "string" || clientId === "") {
    throw new RegistrationError(
      "クライアント登録の応答に client_id がありません",
      "invalid_registration_response",
    );
  }
  return clientId;
}
